import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import Header from './components/Header';
import SchoolSelector from './components/SchoolSelector';
import useConflicts from './hooks/useConflicts';
import useScheduleData from './hooks/useScheduleData';
import usePdfExport from './hooks/usePdfExport';

// Componentes principales hola
const HorarioGrid = lazy(() => import('./components/HorarioGrid'));
const CourseSelector = lazy(() => import('./components/CourseSelector'));
const ConflictAlert = lazy(() => import('./components/ConflictAlert'));
const SelectedCoursesBanner = lazy(() => import('./components/SelectedCoursesBanner'));

// Componente de carga
const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  // Theme toggle
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Persistence
  const [selectedCourses, setSelectedCourses] = useState(() => {
    const saved = localStorage.getItem('selectedCourses');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('selectedCourses', JSON.stringify(selectedCourses));
  }, [selectedCourses]);

  // Estado
  const [selectedScheduleType, setSelectedScheduleType] = useState(null);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfTimeRange, setPdfTimeRange] = useState({ start: null, end: null });
  const scheduleRef = useRef(null);

  // Hooks personalizados
  const conflicts = useConflicts(selectedCourses);
  const { uniqueCourses } = useScheduleData(selectedScheduleType);
  const { exportToPdf, exportToImage, loading: exporting } = usePdfExport();

  // Handlers
  const handleSelectSchool = (type) => {
    setSelectedScheduleType(type);
    setSelectedCourses([]);
    setIsGridVisible(false);
    setSearchTerm('');
  };

  const handleCourseSelection = (course) => {
    const isSelected = selectedCourses.some(
      c => c.Codigo === course.Codigo && c.Seccion === course.Seccion
    );
    if (isSelected) {
      setSelectedCourses(selectedCourses.filter(
        c => !(c.Codigo === course.Codigo && c.Seccion === course.Seccion)
      ));
    } else {
      const related = uniqueCourses.filter(
        c => c.Codigo === course.Codigo && c.Seccion === course.Seccion
      );
      setSelectedCourses([...selectedCourses, ...related]);
    }
  };

  const handleGoBack = () => {
    setSelectedScheduleType(null);
    setSelectedCourses([]);
    setIsGridVisible(false);
  };

  // Calcular rango de tiempo
  useEffect(() => {
    if (selectedCourses.length === 0) {
      setPdfTimeRange({ start: null, end: null });
      return;
    }
    let min = 1440, max = 0;
    selectedCourses.forEach(c => {
      const [h, m] = c['Hora Inicio'].split(':').map(Number);
      const start = h * 60 + m;
      const [eh, em] = c['Hora Final'].split(':').map(Number);
      const end = eh * 60 + em;
      if (start < min) min = start;
      if (end > max) max = end;
    });
    setPdfTimeRange({
      start: `${Math.floor(min / 60).toString().padStart(2, '0')}:${(min % 60).toString().padStart(2, '0')}`,
      end: `${Math.floor(max / 60).toString().padStart(2, '0')}:${(max % 60).toString().padStart(2, '0')}`
    });
  }, [selectedCourses]);

  // Filtrar cursos
  const filteredCourses = searchTerm.length < 3
    ? uniqueCourses
    : uniqueCourses.filter(c =>
        c.Curso.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.Codigo.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300 p-4 md:p-8">
      <Header darkMode={darkMode} setDarkMode={setDarkMode} />

      {!selectedScheduleType ? (
        <SchoolSelector onSelect={handleSelectSchool} />
      ) : (
        <div className="space-y-6">
          <button onClick={handleGoBack} className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
            ⬅️ Volver
          </button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <input
                type="text"
                placeholder="Buscar cursos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
              />
              <Suspense fallback={<LoadingSpinner />}>
                <CourseSelector
                  courses={filteredCourses}
                  selectedCourses={selectedCourses}
                  onSelectCourse={handleCourseSelection}
                />
              </Suspense>
            </div>

            <div className="lg:col-span-2 p-6 bg-white/50 dark:bg-slate-900/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Mi Horario</h2>
                <button
                  onClick={() => setIsGridVisible(true)}
                  disabled={exporting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Generar
                </button>
              </div>

              {isGridVisible && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportToImage(scheduleRef.current, selectedScheduleType)}
                      disabled={exporting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold shadow-lg hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      📸 Imagen
                    </button>
                    <button
                      onClick={() => exportToPdf(scheduleRef.current, selectedScheduleType)}
                      disabled={exporting}
                      className="px-4 py-2 bg-slate-800 text-white rounded-full text-sm font-semibold hover:bg-slate-900 transition-all disabled:opacity-50"
                    >
                      {exporting ? 'Generando...' : 'PDF 🖨️'}
                    </button>
                  </div>

                  <Suspense fallback={<LoadingSpinner />}>
                    <SelectedCoursesBanner
                      selectedCourses={selectedCourses}
                      onClear={() => setSelectedCourses([])}
                    />
                    <ConflictAlert conflicts={conflicts} />
                  </Suspense>

                  <div ref={scheduleRef} className="bg-white dark:bg-slate-900 p-4 rounded-2xl">
                    <Suspense fallback={<LoadingSpinner />}>
                      <HorarioGrid
                        selectedCourses={selectedCourses}
                        pdfTimeRange={pdfTimeRange}
                        conflicts={conflicts}
                      />
                    </Suspense>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;